import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ElevenLabsAgent from './ElevenLabsAgent';
import Turnstile from './Turnstile';
import Icon from './AppIcon';

const ElevenLabsAgentWrapper = ({ onContactClick, onClose, ...props }) => {
  const [consentGiven, setConsentGiven] = useState(false);
  const [turnstileVerified, setTurnstileVerified] = useState(false);
  const [turnstileError, setTurnstileError] = useState(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleConsent = () => {
    setConsentGiven(true);
    setTurnstileError(null);
  };

  const handleTurnstileVerify = (token) => {
    setTurnstileVerified(true);
    setTurnstileError(null);
  };

  const handleTurnstileError = () => {
    setTurnstileError('Verifizierung fehlgeschlagen. Bitte erneut versuchen.');
  };

  const handleTurnstileExpire = () => {
    setTurnstileVerified(false);
    setTurnstileError('Verifizierung abgelaufen. Bitte erneut bestätigen.');
  };

  let content;

  if (!consentGiven) {
    // Phase 1: Consent
    content = (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">KI-Sprachassistent aktivieren</h3>
          <p className="text-sm text-gray-500 leading-relaxed">
            Durch Aktivierung stimme ich zu, dass meine Stimme und Eingaben übertragen und transkribiert werden. Dies umfasst die Verarbeitung biometrischer Daten.
          </p>
          <Link
            to="/datenschutz"
            className="text-sm text-gray-900 underline underline-offset-2 hover:text-gray-600 transition-colors mt-2 inline-block"
            target="_blank"
            rel="noopener noreferrer"
          >
            Datenschutzerklärung
          </Link>
        </div>
        <button
          onClick={handleConsent}
          className="w-full bg-gray-900 text-white px-6 py-3 text-[15px] font-medium rounded-lg hover:bg-gray-800 transition-colors"
        >
          Jetzt aktivieren
        </button>
      </div>
    );
  } else if (!turnstileVerified) {
    // Phase 2: Turnstile
    content = (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Sicherheitsprüfung</h3>
          <p className="text-sm text-gray-500">Bitte bestätigen Sie, dass Sie kein Bot sind.</p>
        </div>
        <div className="flex justify-center">
          <Turnstile
            siteKey={import.meta.env?.VITE_TURNSTILE_SITE_KEY}
            onVerify={handleTurnstileVerify}
            onError={handleTurnstileError}
            onExpire={handleTurnstileExpire}
            theme="light"
            size="normal"
            language="de"
          />
        </div>
        {turnstileError && (
          <p className="text-sm text-red-600 text-center">{turnstileError}</p>
        )}
        <button
          onClick={() => { setConsentGiven(false); setTurnstileVerified(false); setTurnstileError(null); }}
          className="w-full text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors py-2"
        >
          Zurück
        </button>
      </div>
    );
  } else {
    // Phase 3: Agent active
    content = (
      <div>
        <ElevenLabsAgent {...props} onContactClick={onContactClick} />
        <p className="text-xs text-gray-400 text-center mt-4">
          Sie sprechen mit einer KI. Das Gespräch wird zur Analyse transkribiert.
        </p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 10 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="relative w-full max-w-md mx-4 bg-white rounded-xl border border-gray-200 shadow-xl p-8"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100"
          aria-label="Schließen"
        >
          <Icon name="X" size={18} />
        </button>

        {content}
      </motion.div>
    </div>
  );
};

export default ElevenLabsAgentWrapper;
